import { reactive } from 'vue';
import storageService from '@/scripts/services/StorageService.js';
import { settings } from './SettingsService';
import { makeDistortionCurve, generateImpulseResponse, audioBufferToWav } from '@/scripts/utils/audioUtils.js';

// Реактивное состояние плеера
export const playerStore = reactive({
  // Playback state
  isPlaying: false,
  currentTrack: null,
  currentProjectId: null,
  currentTime: 0,
  duration: 0,

  // Playlist
  playlist: [],
  currentPlaylist: [],
  playlistSource: 'adHoc',
  isLoadingPlaylistPage: false,
  shuffle: false,
  unshuffledPlaylist: [],
  isRepeatActive: false,
  isRepeatPlaylist: false,

  // Effects
  pitch: 1,
  tempo: 1,
  gain: 1,
  eq: {
    low: 0,
    mid: 0,
    high: 0,
    lowFrequency: 320,
    midFrequency: 1000,
    highFrequency: 3200,
    lowQ: 1,
    midQ: 1,
    highQ: 1
  },
  reverbMix: 0,
  distortion: 0,
  pan: 0,
  lofiAmount: 0,

  // UI
  showModal: false,
  currentCover: "../img/nocover.png",
  calculatedBpm: null,
  calculatedKey: null,

  // Internal
  audioElement: null,
  audioContext: null,
  wasPlayingBeforeDrag: false,
  currentAudioUrl: null,
  isExporting: false,
  isExportingMp3: false,
});

// Экшены для управления плеером
export const playerActions = {
  _playRequestId: 0,
  _audioContextStateHandler: null,

  // Инициализация (вызывать один раз при монтировании компонента)
  init(audioElement) {
    playerStore.audioElement = audioElement;
  },

  // Воспроизведение трека
  async playTrack(trackId) {
    const requestId = ++this._playRequestId;
    console.log("playTrack", trackId);
    if(!settings.state.persistEffectEachTrack){
      this.resetFx()
    }

    const el = playerStore.audioElement;
    if (!el) {
      console.error('Audio element not initialized');
      return;
    }

    if (playerStore.audioContext && playerStore.audioContext.state === 'suspended') {
      try {
        await playerStore.audioContext.resume();
        if (requestId !== this._playRequestId) return;
        console.log('AudioContext resumed successfully');
      } catch (error) {
        console.error('Failed to resume AudioContext:', error);
      }
      if (requestId !== this._playRequestId) return;
    }

    const storageType = storageService.state.storageType;
    const tracks = storageService.state.tracks;
    const projects = storageService.state.projects;
    const sharedTracks = storageService.state.sharedTracks;


    let track = playerStore.currentPlaylist.find(t => t.id === trackId);

    if (!track) {
      track = tracks.find(t => t.id === trackId);
    }

    if (!track) {
      projects.forEach(project => {
        const projectTracks = project.tracks || [];
        projectTracks.forEach(projectTrack => {
          if (projectTrack.id === trackId) {
            track = projectTrack;
          }
        })
      }
      )
    }

    if(!track) {
      track = sharedTracks.find(t => t.id === trackId);
    }

    if (!track) {
      console.error('Track not found:', trackId);
      return;
    }
    playerStore.currentTrack = track;
    if (storageType === 'remote' || storageType === 'shared') {
      // Get signed URL for the audio file
      const fileId = typeof track.file === 'object' ? track.file.id : track.file;
      const audioUrl = await storageService.getFileLink(fileId);
      if (requestId !== this._playRequestId) return;
      if (!audioUrl) {
        console.error('Failed to get audio URL for track:', trackId);
        return;
      }
      el.src = audioUrl;
      el.load();
      await this._playElement(el, requestId);
    } else if (storageType === 'local') {
      const fileId = typeof track.file === 'object' ? track.file.id : track.file;
      if (!fileId) {
        console.error('Missing file id for track:', trackId);
        return;
      }
      const fileData = await storageService.getFile(fileId);
      if (requestId !== this._playRequestId) return;
      if (!fileData) {
        console.error('Audio file not found in local storage:', fileId);
        return;
      }
      const mimeType = track.type || 'audio/mpeg';
      const audioUrl = typeof fileData === 'string' && fileData.startsWith('data:')
        ? fileData
        : URL.createObjectURL(new Blob([fileData], { type: mimeType }));

      if (playerStore.currentAudioUrl) {
        URL.revokeObjectURL(playerStore.currentAudioUrl);
      }

      playerStore.currentAudioUrl = audioUrl;
      el.src = audioUrl;
      el.load();
      await this._playElement(el, requestId);
    }
  },

  // Play/Pause
  async togglePlay() {
    const el = playerStore.audioElement;
    if (!el) return;

    if (playerStore.isPlaying) {
      this._playRequestId++;
      el.pause();
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
    } else {
      const requestId = ++this._playRequestId;
      const canPlay = await this.ensureAudioContextRunning();
      if (!canPlay) return;
      if (requestId !== this._playRequestId) return;
      await this._playElement(el, requestId);
    }
  },

  async _playElement(el, requestId) {
    try {
      await el.play();
      if (requestId !== this._playRequestId) return false;
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing';
      }
      return true;
    } catch (error) {
      if (requestId !== this._playRequestId) return false;
      playerStore.isPlaying = false;
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
      console.warn('Audio playback was blocked or failed:', error);
      return false;
    }
  },

  // Seek
  seekTo(time) {
    const el = playerStore.audioElement;
    if (el) {
      el.currentTime = time;
    }
  },

  // Playlist navigation
  async playNextTrack() {
    this.syncStandalonePlaylist();
    let currentPlaylist = playerStore.currentPlaylist;
    const currentTrack = playerStore.currentTrack;

    if (!currentTrack || !currentPlaylist.length) return;

    for (let index = 0; index < currentPlaylist.length; index++) {
      const element = currentPlaylist[index];
      if (element.id === currentTrack.id) {
        if (index === currentPlaylist.length - 1 && playerStore.playlistSource === 'standalone') {
          if (storageService.state.trackPagination.hasMore && !playerStore.isLoadingPlaylistPage) {
            playerStore.isLoadingPlaylistPage = true;
            try {
              await storageService.loadStandaloneTracks();
              this.syncStandalonePlaylist();
              currentPlaylist = playerStore.currentPlaylist;
              const refreshedIndex = currentPlaylist.findIndex(track => track.id === currentTrack.id);
              if (refreshedIndex !== -1 && refreshedIndex + 1 < currentPlaylist.length) {
                this.playTrack(currentPlaylist[refreshedIndex + 1].id);
                return;
              }
            } finally {
              playerStore.isLoadingPlaylistPage = false;
            }
          }
          if (storageService.state.trackPagination.hasMore) return;
        }
        if (index === currentPlaylist.length - 1) {
          if (playerStore.isRepeatPlaylist) {
            const nexttrack = currentPlaylist[0];
            this.playTrack(nexttrack.id);
          }
        } else {
          const nexttrack = currentPlaylist[index + 1];
          this.playTrack(nexttrack.id);
        }
        break;
      }
    }
  },

  playPreviousTrack() {
    const currentPlaylist = playerStore.currentPlaylist;
    const currentTrack = playerStore.currentTrack;

    if (!currentTrack || !currentPlaylist.length) return;

    for (let index = 0; index < currentPlaylist.length; index++) {
      const element = currentPlaylist[index];
      if (element.id === currentTrack.id) {
        if (index === 0) {
          if (playerStore.isRepeatPlaylist) {
            const prevTrack = currentPlaylist[currentPlaylist.length - 1];
            this.playTrack(prevTrack.id);
          }
        } else {
          const prevTrack = currentPlaylist[index - 1];
          this.playTrack(prevTrack.id);
        }
        break;
      }
    }
  },

  toggleRepeat() {
    if (!playerStore.isRepeatPlaylist && !playerStore.isRepeatActive) {
      playerStore.isRepeatPlaylist = true;
      return;
    }

    if (playerStore.isRepeatPlaylist) {
      playerStore.isRepeatPlaylist = false;
      playerStore.isRepeatActive = true;
      return;
    }

    playerStore.isRepeatActive = false;
    playerStore.isRepeatPlaylist = false;
  },

  handleTrackEnded() {
    playerStore.isPlaying = false;
    playerStore.currentTime = 0;

    if (playerStore.isRepeatActive) {
      const el = playerStore.audioElement;
      if (!el) return;

      el.currentTime = 0;
      const requestId = ++this._playRequestId;
      this._playElement(el, requestId);
      return;
    }

    this.playNextTrack();
  },

  resetFx(){
    playerStore.pitch = 1
    playerStore.tempo = 1
    playerStore.gain = 1
    playerStore.eq = {
      low: 0,
      mid: 0,
      high: 0,
      lowFrequency: 320,
      midFrequency: 1000,
      highFrequency: 3200,
      lowQ: 1,
      midQ: 1,
      highQ: 1
    }
    playerStore.reverbMix = 0
    playerStore.distortion = 0
    playerStore.pan = 0
    playerStore.lofiAmount = 0
  },

  // Shuffle
  toggleShuffle() {
    if (!playerStore.shuffle) {
      playerStore.unshuffledPlaylist = [...playerStore.currentPlaylist];

      // Fisher-Yates shuffle
      const playlist = [...playerStore.currentPlaylist];
      for (let i = playlist.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [playlist[i], playlist[j]] = [playlist[j], playlist[i]];
      }

      playerStore.currentPlaylist = playlist;
      playerStore.shuffle = true;
    } else {
      playerStore.currentPlaylist = [...playerStore.unshuffledPlaylist];
      playerStore.shuffle = false;
    }
  },

  async ensureAudioContextRunning() {
    if (playerStore.audioContext && playerStore.audioContext.state === 'suspended') {
      try {
        await playerStore.audioContext.resume();
        console.log('AudioContext resumed');
        return true;
      } catch (error) {
        console.error('Failed to resume AudioContext:', error);
        return false;
      }
    }
    return true;
  },

  // Set playlist
  setPlaylist(playlist, projectId = null, source = projectId ? 'project' : 'adHoc') {
    playerStore.currentProjectId = projectId;
    playerStore.playlistSource = source;
    playerStore.playlist = playlist;
    playerStore.currentPlaylist = [...playlist];
  },

  syncStandalonePlaylist() {
    if (playerStore.playlistSource !== 'standalone') return;
    const knownIds = new Set(playerStore.currentPlaylist.map(track => track.id));
    const missing = storageService.state.tracks.filter(track => !knownIds.has(track.id));
    if (!missing.length) return;

    if (playerStore.shuffle) {
      for (let index = missing.length - 1; index > 0; index--) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [missing[index], missing[swapIndex]] = [missing[swapIndex], missing[index]];
      }
    }
    playerStore.currentPlaylist = [...playerStore.currentPlaylist, ...missing];
    playerStore.playlist = [...storageService.state.tracks];
    playerStore.unshuffledPlaylist = [...storageService.state.tracks];
  },

  syncTrack(track) {
    if (!track?.id) return;

    const replaceTrack = list => (list || []).map(item => item.id === track.id ? track : item);
    if (playerStore.currentTrack?.id === track.id) {
      playerStore.currentTrack = track;
    }
    playerStore.playlist = replaceTrack(playerStore.playlist);
    playerStore.currentPlaylist = replaceTrack(playerStore.currentPlaylist);
    playerStore.unshuffledPlaylist = replaceTrack(playerStore.unshuffledPlaylist);
  },

  // Drag handlers
  handleDragStart() {
    if (playerStore.isPlaying) {
      playerStore.wasPlayingBeforeDrag = true;
    }
  },

  // Audio context (вызывать после инициализации audioElement)
  setAudioContext(context) {
    // Remove old listener if context is being replaced
    if (playerStore.audioContext && this._audioContextStateHandler) {
      playerStore.audioContext.removeEventListener('statechange', this._audioContextStateHandler);
    }

    playerStore.audioContext = context;

    // Store handler reference for cleanup
    this._audioContextStateHandler = () => {
      console.log('statechanged')
      this.ensureAudioContextRunning()
    };
    context.addEventListener('statechange', this._audioContextStateHandler);
  },

  // Cleanup audio context listener (call before destroying)
  cleanupAudioContext() {
    if (playerStore.audioContext && this._audioContextStateHandler) {
      playerStore.audioContext.removeEventListener('statechange', this._audioContextStateHandler);
      this._audioContextStateHandler = null;
    }
  },

  async exportTrackWithEffects(track = null, format = 'wav', withEffects = true, download = true) {
    const trackToExport = track || playerStore.currentTrack;
    if (!trackToExport || playerStore.isExporting || playerStore.isExportingMp3) {
      return;
    }

    console.log(`Starting track export as ${format}${withEffects ? ' with effects' : ' (no effects)'}...`);
    if (format === 'wav') {
      playerStore.isExporting = true;
    } else {
      playerStore.isExportingMp3 = true;
    }

    try {
      // 1. Get Audio Data
      const track = trackToExport;
      let audioBlob;

      if (storageService.state.storageType === 'local') {
        const fileData = await storageService.getFile(track.file);
        if (!fileData) throw new Error("Failed to load local file.");
        audioBlob = new Blob([fileData], { type: track.type || 'audio/mpeg' });
      } else { // remote or shared
        // Get signed URL for the audio file
        const fileId = typeof track.file === 'object' ? track.file.id : track.file;
        const audioUrl = await storageService.getFileLink(fileId);
        if (!audioUrl) throw new Error("Failed to get audio URL.");
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error(`Failed to fetch audio file: ${response.statusText}`);
        audioBlob = await response.blob();
      }
      const rawAudioData = await audioBlob.arrayBuffer();

      // 2. Decode Audio Data
      const tempCtx = new (window.AudioContext || window.webkitAudioContext)();
      const decodedAudioData = await tempCtx.decodeAudioData(rawAudioData);
      await tempCtx.close();

      let renderedBuffer;

      if (withEffects) {
        // 3. Setup OfflineAudioContext with effects
        const newLength = Math.floor(decodedAudioData.length / playerStore.tempo);
        const offlineCtx = new OfflineAudioContext(
          decodedAudioData.numberOfChannels,
          newLength,
          decodedAudioData.sampleRate
        );

        // 4. Rebuild the audio node chain
        await offlineCtx.audioWorklet.addModule('/worklets/soundtouch-worklet.js');
        const soundtouchNode = new AudioWorkletNode(offlineCtx, 'soundtouch-processor');

        await offlineCtx.audioWorklet.addModule('/worklets/bitcrusher-worklet.js');
        const bitcrusherNode = new AudioWorkletNode(offlineCtx, 'bitcrusher-processor');

        const distortionNode = offlineCtx.createWaveShaper();
        const lowShelfFilter = offlineCtx.createBiquadFilter();
        const midPeakingFilter = offlineCtx.createBiquadFilter();
        const highShelfFilter = offlineCtx.createBiquadFilter();
        const convolverNode = offlineCtx.createConvolver();
        const dryGain = offlineCtx.createGain();
        const wetGain = offlineCtx.createGain();
        const splitter = offlineCtx.createChannelSplitter(2);
        const merger = offlineCtx.createChannelMerger(2);
        const leftGain = offlineCtx.createGain();
        const rightGain = offlineCtx.createGain();
        const finalGain = offlineCtx.createGain();

        // 5. Configure nodes with parameters
        soundtouchNode.parameters.get('tempo').value = playerStore.tempo;
        soundtouchNode.parameters.get('pitch').value = playerStore.pitch;

        bitcrusherNode.parameters.get('dryWet').value = playerStore.lofiAmount;
        distortionNode.curve = makeDistortionCurve(playerStore.distortion);

        lowShelfFilter.type = 'peaking';
        lowShelfFilter.frequency.value = playerStore.eq.lowFrequency ?? 320;
        lowShelfFilter.Q.value = playerStore.eq.lowQ ?? 1;
        lowShelfFilter.gain.value = playerStore.eq.low;

        midPeakingFilter.type = 'peaking';
        midPeakingFilter.frequency.value = playerStore.eq.midFrequency ?? 1000;
        midPeakingFilter.Q.value = playerStore.eq.midQ ?? 1;
        midPeakingFilter.gain.value = playerStore.eq.mid;

        highShelfFilter.type = 'peaking';
        highShelfFilter.frequency.value = playerStore.eq.highFrequency ?? 3200;
        highShelfFilter.Q.value = playerStore.eq.highQ ?? 1;
        highShelfFilter.gain.value = playerStore.eq.high;

        convolverNode.buffer = generateImpulseResponse(offlineCtx, 2, 2);

        dryGain.gain.value = 1 - playerStore.reverbMix;
        wetGain.gain.value = playerStore.reverbMix;

        const pan = playerStore.pan;
        leftGain.gain.value = pan <= 0 ? 1 : 1 - pan;
        rightGain.gain.value = pan >= 0 ? 1 : 1 + pan;

        finalGain.gain.value = playerStore.gain;

        // 6. Connect nodes
        const source = offlineCtx.createBufferSource();
        source.buffer = decodedAudioData;

        source.connect(soundtouchNode)
              .connect(bitcrusherNode)
              .connect(distortionNode)
              .connect(lowShelfFilter)
              .connect(midPeakingFilter)
              .connect(highShelfFilter);

        highShelfFilter.connect(splitter);
        splitter.connect(leftGain, 0);
        leftGain.connect(merger, 0, 0);
        splitter.connect(rightGain, 1);
        rightGain.connect(merger, 0, 1);

        merger.connect(dryGain);
        merger.connect(convolverNode);
        convolverNode.connect(wetGain);

        dryGain.connect(finalGain);
        wetGain.connect(finalGain);
        finalGain.connect(offlineCtx.destination);

        // 7. Render audio
        source.start(0);
        renderedBuffer = await offlineCtx.startRendering();
      } else {
        // Export without effects - just use the decoded audio as-is
        renderedBuffer = decodedAudioData;
      }

      // 8. Convert to the requested format
      let outputBlob, extension;
      if (format === 'wav') {
        outputBlob = audioBufferToWav(renderedBuffer);
        extension = 'wav';
      } else {
        const { Mp3Encoder } = await import('@breezystack/lamejs');
        const encoder = new Mp3Encoder(renderedBuffer.numberOfChannels, renderedBuffer.sampleRate, 320);
        const pcmLeft = renderedBuffer.getChannelData(0);
        const pcmRight = renderedBuffer.numberOfChannels > 1 ? renderedBuffer.getChannelData(1) : pcmLeft;
        
        const samples = new Int16Array(pcmLeft.length);
        for(let i = 0; i < pcmLeft.length; i++) {
            samples[i] = pcmLeft[i] * 32767;
        }
        const samplesRight = new Int16Array(pcmRight.length);
        for(let i = 0; i < pcmRight.length; i++) {
            samplesRight[i] = pcmRight[i] * 32767;
        }

        const mp3Data = [];
        const blockSize = 1152;
        for (let i = 0; i < samples.length; i += blockSize) {
            const leftChunk = samples.subarray(i, i + blockSize);
            const rightChunk = samplesRight.subarray(i, i + blockSize);
            const mp3buf = encoder.encodeBuffer(leftChunk, rightChunk);
            if (mp3buf.length > 0) {
                mp3Data.push(mp3buf);
            }
        }
        const mp3buf = encoder.flush();
        if (mp3buf.length > 0) {
            mp3Data.push(mp3buf);
        }

        outputBlob = new Blob(mp3Data, { type: 'audio/mp3' });
        extension = 'mp3';
      }
  
      const newFileName = `${track.name || 'track'}${withEffects ? '_fx' : ''}.${extension}`;
      if (download) {
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(outputBlob);
        downloadLink.download = newFileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadLink.href);
      }
  
      console.log(`Track export as ${format} finished.`);
      return { blob: outputBlob, extension, filename: newFileName };
  
    } catch (error) {
      console.error(`Error exporting track as ${format}:`, error);
      return null;
    } finally {
      if (format === 'wav') {
        playerStore.isExporting = false;
      } else {
        playerStore.isExportingMp3 = false;
      }
    }
  }

};
